import { cubicEaseOut, FadeUpActive, FadeUpBase } from '@helpers/animation';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import global, { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import SectionWrapper from '@stories/Components/Global/SectionWrapper/SectionWrapper';
import SiteWideStyles from '@stories/Components/Global/SiteWide/SiteWide.styles';
import styled, { css, keyframes } from 'styled-components';
const Container = styled(SectionWrapper) `
  position: relative;
  display: grid;
  background: ${brand.black};
  min-height: 405px;

  @media ${from(Device.TabletLarge)} {
    min-height: 600px;
  }
`;
const Item = styled.div `
  position: relative;
  display: grid;
  grid-column: 1;
  grid-row: 1;
  opacity: 0;
  pointer-events: none;
  text-align: center;
  transition: opacity 0.3s ${cubicEaseOut};

  &[aria-current='true'] {
    opacity: 1;
    pointer-events: all;
  }

  @media ${from(Device.TabletLarge)} {
    place-content: center;
    place-items: center;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      display: block;
      background: ${brand.black};
      height: 100%;
      opacity: var(--tint);
      width: 100%;
      z-index: 2;
    }
  }
`;
const ImageWrapper = styled.div `
  z-index: 1;

  @media ${until(Device.TabletLarge)} {
    position: relative;
    display: block;
    height: 0;
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 1) 90%,
      rgba(0, 0, 0, 0) 100%
    );
    padding-bottom: ${(280 / 375) * 100}%;
    width: 100%;

    @media ${from(Device.Tablet)} {
      padding-bottom: ${(9 / 16) * 100}%;
    }
  }

  @media ${from(Device.TabletLarge)} {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    height: 100%;
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 1) 90%,
      rgba(0, 0, 0, 0) 100%
    );
    width: 100%;
  }
`;
const mediaStyles = css `
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  object-fit: cover;
  object-position: center;
  width: 100%;
`;
const Image = styled.img `
  ${mediaStyles}
  display: block;

  ${ImageWrapper}[data-has-video="true"] & {
    @media ${from(Device.Tablet)} {
      display: none;
    }
  }
`;
const Video = styled.div `
  ${mediaStyles};

  display: none;

  @media ${from(Device.Tablet)} {
    display: block;

    video,
    iframe {
      height: 100%;
      object-fit: cover;
      object-position: center;
      width: 100%;
    }
  }
`;
const ContentWrapper = styled.div `
  --a-duration: 0.3s;

  color: ${brand.white};
  z-index: 3;

  @media ${until(Device.TabletLarge)} {
    margin: 0 auto;
    margin-top: -16px;
    max-width: 600px;
    padding-left: ${global.siteWidePaddingMobile}px;
    padding-right: ${global.siteWidePaddingMobile}px;
    padding-bottom: 42px;
  }

  @media ${from(Device.TabletLarge)} {
    position: relative;
    max-width: 848px;
  }
`;
const Title = styled.h2 `
  ${fonts.inter.extraBold};

  font-size: 26px;
  letter-spacing: 0.01em;
  line-height: 1em;
  margin: 0 0 8px;
  text-transform: uppercase;

  @media ${from(Device.TabletLarge)} {
    font-size: 60px;
    margin: 0 0 16px;
  }

  --a-delay: 0.15s;

  ${FadeUpBase}

  [aria-current='true'] & {
    ${FadeUpActive}
  }
`;
const Content = styled.p `
  ${fonts.sofiaPro.regular};
  font-size: 21px;
  letter-spacing: 0.01em;
  line-height: 28px;
  margin: 0 0 24px;

  @media ${from(Device.TabletLarge)} {
    font-size: 24px;
    margin: 0 0 24px;
  }

  --a-delay: 0.3s;

  ${FadeUpBase}

  [aria-current='true'] & {
    ${FadeUpActive}
  }
`;
const CtaWrapper = styled.div `
  --a-delay: 0.45s;

  ${FadeUpBase}

  [aria-current='true'] & {
    ${FadeUpActive}
  }
`;
const Controls = styled.div `
  position: absolute;
  top: 25%;
  transform: translateY(-50%);
  width: 100%;
  z-index: 4;

  ${SiteWideStyles.Container} {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
  }

  @media ${from(Device.MobileLarge)} {
    top: 30%;
  }

  @media ${from(Device.Tablet)} {
    top: 35%;
  }

  @media ${from(Device.TabletLarge)} {
    top: 50%;
  }
`;
const ControlItem = styled.button `
  ${ButtonReset}

  position: relative;
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  transform-origin: center right;
  transition: transform 0.3s ${cubicEaseOut};
  width: 18px;

  &:focus,
  &:hover {
    transform: scaleX(1.5);
  }

  &[aria-current='true'] {
    transform: scaleX(2);
  }

  @media ${from(Device.TabletLarge)} {
    width: 25px;
  }
`;
const BaseLine = styled.span `
  position: absolute;
  top: 4px;
  left: 0;
  display: block;
  background: ${brand.white};
  height: 3px;
  transform-origin: center right;
  width: 100%;
  z-index: 1;
`;
const HighlightKeyframes = keyframes `
  0% {
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(1);
  }
`;
const Highlight = styled(BaseLine) `
  display: none;
  background: ${brand.primary.blue.highlight};
  z-index: 2;

  [data-autoplay='true'] [aria-current='true'] & {
    display: block;
  }

  [aria-current='true'] & {
    animation: ${HighlightKeyframes} linear;
    animation-duration: var(--autoplay-interval);
  }
`;
const Label = styled.span `
  ${srOnly}
`;
const LandingPageHeroBannerStyles = {
    BaseLine,
    Container,
    Content,
    ContentWrapper,
    Controls,
    ControlItem,
    CtaWrapper,
    Highlight,
    Image,
    ImageWrapper,
    Item,
    Label,
    Title,
    Video,
};
export default LandingPageHeroBannerStyles;
